/**
 * Round-result derivation — shared shape with
 * teenpatti-game/src/components/resultModel.js.
 *
 * Everything here turns the server's authoritative `winners[]` payload into
 * what the result panel renders. The rule the old inline code broke: the
 * displayed payout must be the one the settle transaction actually credited,
 * never a client-side `stake × multiplier` guess. The estimate survives only as
 * a labelled fallback for when we don't yet know our own user id.
 *
 * Ferris's equivalent lives in ferris-game/src/sheet.js (buildTopWinners at
 * :158-183, formatNum at :230-233). Its `escapeHtml` is deliberately absent —
 * this renders through React, which escapes for us.
 */

/**
 * @param {object}   args
 * @param {Array|null} args.winners       authoritative winning bets, or null while pending
 * @param {string|null} args.currentUserId our own uid, or null if not yet known
 * @param {object}   args.myStakes        { [frameId]: amount } for this round
 * @param {function} args.estimatePayout  fallback used only when the authoritative path is unavailable
 * @returns {{ state: 'win'|'lose'|'none', staked: number, payout: number, net: number, authoritative: boolean }}
 */
export function buildMyResult({ winners, currentUserId, myStakes, estimatePayout }) {
  // Every stake counts against us, including the ones on frames that lost —
  // `payout` on a bet doc is gross, credited on top of an already-debited stake.
  const staked = Object.values(myStakes || {}).reduce(
    (sum, n) => sum + Math.max(0, Math.floor(Number(n) || 0)),
    0
  );

  if (staked <= 0) {
    return { state: 'none', staked: 0, payout: 0, net: 0, authoritative: true };
  }

  let payout;
  let authoritative;
  if (currentUserId && Array.isArray(winners)) {
    // A single number can win several frames at once (Red + Odd + a Dozen), so
    // there may be more than one row for us. Sum them; never take the first.
    payout = winners.reduce((sum, w) => (
      w && String(w.userId) === String(currentUserId)
        ? sum + Math.max(0, Math.floor(Number(w.payout) || 0))
        : sum
    ), 0);
    authoritative = true;
  } else {
    payout = Math.max(0, Math.floor(Number(estimatePayout && estimatePayout()) || 0));
    authoritative = false;
  }

  return {
    state: payout > 0 ? 'win' : 'lose',
    staked,
    payout,
    net: payout - staked,
    authoritative,
  };
}

/**
 * Collapse the winning bets into a ranked top three, one entry per player.
 * Port of ferris-game/src/sheet.js:158-183.
 */
export function buildTopWinners(winners, currentUserId) {
  if (!Array.isArray(winners)) return null;
  const byUser = new Map();
  for (const winner of winners) {
    if (!winner || Number(winner.payout || 0) <= 0) continue;
    const userId = String(winner.userId || '').trim();
    if (!userId) continue;
    const current = byUser.get(userId) || {
      userId,
      userName: winner.userName || 'Player',
      userAvatar: winner.userAvatar || '',
      payout: 0,
      betAmount: 0,
      isMe: currentUserId != null && userId === String(currentUserId),
    };
    current.payout += Math.max(0, Math.floor(Number(winner.payout) || 0));
    current.betAmount += Math.max(0, Math.floor(Number(winner.amount) || 0));
    if (!current.userAvatar && winner.userAvatar) current.userAvatar = winner.userAvatar;
    if ((!current.userName || current.userName === 'Player') && winner.userName) {
      current.userName = winner.userName;
    }
    byUser.set(userId, current);
  }
  return Array.from(byUser.values())
    .sort((a, b) => (b.payout - a.payout)
      || (b.betAmount - a.betAmount)
      || String(a.userName).localeCompare(String(b.userName)))
    .slice(0, 3)
    .map((winner, index) => ({ ...winner, rank: index + 1 }));
}

/** Thousands-separated integer. Port of ferris-game/src/sheet.js:230-233. */
export function formatNum(n) {
  if (n == null || isNaN(n)) return '—';
  const v = Math.floor(Math.abs(Number(n)));
  return String(v).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** Signed rendering for the net line: `+1,200` / `−800` / `0`. */
export function formatSigned(n) {
  const v = Math.floor(Number(n) || 0);
  if (v === 0) return '0';
  return `${v > 0 ? '+' : '−'}${formatNum(v)}`;
}
